import { Icon24ChevronUpSmall } from '@vkontakte/icons';
import { Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import blockQuotaIllustration from 'img/quota-landing/beautifulIllustrations/blockQuotaIllustration.png';
import largeFilesIllustration from 'img/quota-landing/beautifulIllustrations/largeFilesIllustration.png';
import noAdIllustration from 'img/quota-landing/beautifulIllustrations/noAdIllustration.png';
import React, { type ReactElement, forwardRef, memo, useCallback, useMemo, useState } from 'react';
import { autoUploadInfoBlock, helpAutoUploadHref } from 'reactApp/appHelpers/featuresHelpers/features/autoUploadPromo';
import { promocodeInOptions } from 'reactApp/appHelpers/featuresHelpers/features/promocodeOnPromoQuota';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { Container } from 'reactApp/sections/QuotaLanding/Container/Container';
import { useIntersecObsrWithParams } from 'reactApp/sections/QuotaLanding/hooks/useIntersecObsrWithParams';
import { openPromocodeModal, sendPromocodeButtonAnalytics } from 'reactApp/sections/QuotaLanding/PromocodeDialog/PromocodeDialog.helpers';
import { sendQuotaBlockViewDWH } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';

import styles from './BeautifulIllustrations.css';
import { UnlimUploadInfo } from './UnlimUploadInfo';

interface BeautifulIllustrationsProps {
    noMarginBottom?: boolean;
    headerHeight?: number;
    scrollToTariffs: () => void;
    unlimitedPromoRef: React.RefObject<HTMLDivElement>;
    mayPromo2025Enabled?: boolean;
}

const BigFilesInfo = memo(() => (
    <div className={styles.card}>
        <img className={styles.largeFilesIllustration} src={largeFilesIllustration} alt="Cover image" />
        <div className={styles.cardContent}>
            <Text className={styles.cardTitle}>Обмен тяжёлыми файлами</Text>
            <Text className={styles.cardText}>
                Фотографии, видео и&nbsp;даже игры&nbsp;— загружайте в&nbsp;Облако и&nbsp;отправляйте в&nbsp;письмах большие
                файлы&nbsp;размером до&nbsp;100&nbsp;ГБ
            </Text>
        </div>
    </div>
));

BigFilesInfo.displayName = 'BigFilesInfo';

export const MayPromo2025UnlimitBlock = forwardRef<HTMLDivElement, { scrollToTariffs: () => void }>(({ scrollToTariffs }, ref) => {
    const isMobile = EnvironmentSelectors.isMobile();
    const [expanded, setExpanded] = useState(false);

    return (
        <div
            className={classNames(styles.card, styles.noGap, styles.mayPromo2025, {
                [styles.mayPromo2025Mobile]: isMobile,
                [styles.rootExpanded]: expanded,
            })}
            ref={ref}
        >
            <div className={styles.cardContent}>
                <Text className={styles.cardTitle}>Безлимит для фото и&nbsp;видео</Text>
                <Text className={styles.cardText}>
                    Получите неограниченное{isMobile ? <br /> : ' '}место в&nbsp;Облаке для&nbsp;загрузки
                    <br />
                    фото и&nbsp;видео с&nbsp;телефона&nbsp;—{isMobile ? <br /> : ' '}с&nbsp;любым тарифом от&nbsp;256&nbsp;ГБ
                </Text>

                <div
                    className={classNames(styles.expandedContent, {
                        [styles.expanded]: expanded,
                    })}
                >
                    <Text className={styles.expandedTitle}>Что сделать, чтобы{isMobile ? <br /> : ' '}заработал безлимит</Text>
                    <div className={styles.expandedSteps}>
                        <div className={styles.step}>
                            <div className={styles.stepNumber}>1</div>
                            <Text className={styles.stepText}>Подключите любой тариф Mail&nbsp;Space от&nbsp;256&nbsp;ГБ</Text>
                        </div>
                        <div className={styles.step}>
                            <div className={styles.stepNumber}>2</div>
                            <Text className={styles.stepText}>Включите безлимитную автозагрузку в&nbsp;приложении Облака</Text>
                        </div>
                        <div className={styles.step}>
                            <div className={styles.stepNumber}>3</div>
                            <Text className={styles.stepText}>
                                Снимайте нон-стоп&nbsp;— всё&nbsp;загрузится в&nbsp;Облако, но&nbsp;память не&nbsp;затронет
                            </Text>
                        </div>
                    </div>
                    <Text className={styles.expandedDescription}>
                        {isMobile ? (
                            <>
                                <a href={helpAutoUploadHref} className={styles.link} target="_blank" rel="noopener noreferrer">
                                    Подробные условия
                                </a>{' '}
                            </>
                        ) : (
                            <>
                                Подробнее{' '}
                                <a href={helpAutoUploadHref} className={styles.link} target="_blank" rel="noopener noreferrer">
                                    рассказали в статье
                                </a>{' '}
                                «Как включить безлимитную автозагрузку файлов с телефона в Облако Mail»
                            </>
                        )}
                    </Text>
                </div>

                <div className={styles.buttons}>
                    <Button className={styles.button} primary theme="vk" onClick={scrollToTariffs}>
                        Выбрать тариф
                    </Button>
                    <Button
                        className={classNames(styles.button, {
                            [styles.expandedButton]: expanded,
                        })}
                        theme="vk"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? <Icon24ChevronUpSmall /> : 'Как это работает'}
                    </Button>
                </div>
            </div>
        </div>
    );
});

MayPromo2025UnlimitBlock.displayName = 'MayPromo2025UnlimitBlock';

export const BeautifulIllustrations = forwardRef<HTMLDivElement, BeautifulIllustrationsProps>(
    ({ noMarginBottom, headerHeight, scrollToTariffs, unlimitedPromoRef, mayPromo2025Enabled }, ref): ReactElement => {
        const onIntersecting = useCallback(() => {
            sendQuotaBlockViewDWH('advantages');
            sendPromocodeButtonAnalytics('show', 'options', 'desktop');

            if (autoUploadInfoBlock) {
                sendQuotaBlockViewDWH('unlimited_autoupload');
            }
        }, []);

        const refIntersection = useIntersecObsrWithParams({
            onIntersecting,
            isMobile: false,
            headerHeight,
        });

        const handlePromocode = useCallback(() => {
            openPromocodeModal();
            sendPromocodeButtonAnalytics('click', 'options', 'desktop');
        }, []);

        const bottomBlock = useMemo(() => {
            if (mayPromo2025Enabled) {
                return <MayPromo2025UnlimitBlock scrollToTariffs={scrollToTariffs} ref={unlimitedPromoRef} />;
            }

            if (autoUploadInfoBlock) {
                return (
                    <div ref={unlimitedPromoRef} className={classNames(styles.card, styles.noGap, styles.cardAccent)}>
                        <UnlimUploadInfo />
                    </div>
                );
            }

            return <BigFilesInfo />;
        }, [scrollToTariffs]);

        return (
            <div ref={refIntersection}>
                <div ref={ref}>
                    <Container isNarrow>
                        <div
                            className={classNames(styles.root, {
                                [styles.root_noMarginBottom]: noMarginBottom,
                            })}
                        >
                            <div className={styles.card}>
                                <img className={styles.blockQuotaIllustration} src={blockQuotaIllustration} alt="Cover image" />
                                <div className={styles.cardContent}>
                                    <Text className={styles.cardTitle}>Много места. Очень</Text>
                                    <Text className={styles.cardText}>
                                        Собирайте тарифы как конструктор: легко добавляйте место в Облако для&nbsp;личного и рабочих
                                        проектов
                                    </Text>
                                </div>
                            </div>
                            <div className={styles.card}>
                                <img className={styles.noAdIllustration} src={noAdIllustration} alt="Cover image" />
                                <Spacing size={16} />
                                <div className={styles.cardContent}>
                                    <Text className={styles.cardTitle}>Никакой рекламы</Text>
                                    <Text className={styles.cardText}>
                                        Будьте уверены в том, что&nbsp;контекстная реклама не&nbsp;будет отвлекать и&nbsp;не&nbsp;выдаст
                                        ваши планы
                                    </Text>
                                </div>
                            </div>
                            {bottomBlock}
                        </div>
                        {promocodeInOptions && (
                            <>
                                <Spacing size={40} />
                                <div className={styles.buttonWrapper}>
                                    <Button
                                        primary
                                        theme="vk"
                                        sizeMode={ButtonSizeMode.extraBig}
                                        className={styles.button}
                                        onClick={scrollToTariffs}
                                    >
                                        Выбрать подписку Mail Space
                                    </Button>
                                    <Button
                                        theme="vk"
                                        sizeMode={ButtonSizeMode.extraBig}
                                        className={styles.button}
                                        onClick={handlePromocode}
                                    >
                                        Ввести промокод
                                    </Button>
                                </div>
                            </>
                        )}
                    </Container>
                </div>
            </div>
        );
    }
);

BeautifulIllustrations.displayName = 'BeautifulIllustrations';
